import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Sparkles, Star, Heart, User, Users, LogOut, 
  Calculator, Settings, ChevronRight, MessageCircle 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NumerologyCard = ({ title, number, description, icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="ethereal-card rounded-2xl p-6"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F3E5AB]/50 to-[#D4AF37]/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <div className="number-circle w-14 h-14 text-xl">
        {number}
      </div>
    </div>
    <h3 className="font-heading text-lg text-[#2D2A26] mb-2">{title}</h3>
    <p className="text-[#8C857B] text-sm font-light leading-relaxed">{description}</p>
  </motion.div>
);

export const DashboardPage = () => {
  const { user, token, logout } = useAuth();
  const [meanings, setMeanings] = useState(null);
  const [topMatches, setTopMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meaningsRes, matchesRes] = await Promise.all([
          axios.get(`${API}/numerology/meanings`),
          axios.get(`${API}/matches`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setMeanings(meaningsRes.data);
        setTopMatches(matchesRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingMatches(false);
      }
    };
    fetchData();
  }, [token]);

  const getNumberMeaning = (type, number) => {
    if (!meanings || !meanings[type]) return '';
    return meanings[type][number] || meanings[type][number % 10] || '';
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-xl border-b border-[#E8E2D2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="dashboard-logo">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl text-[#2D2A26]">Intuitive Matchmaker</span>
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link 
                to="/matches" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-matches"
              >
                <Users className="w-4 h-4" />
                Matches
              </Link>
              <Link 
                to="/messages" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-messages"
              >
                <MessageCircle className="w-4 h-4" />
                Messages
              </Link>
              <Link 
                to="/calculator" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-calculator"
              >
                <Calculator className="w-4 h-4" />
                Calculator
              </Link>
              <Link 
                to="/settings" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-settings"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Link>
              <button 
                onClick={logout}
                className="text-[#8C857B] hover:text-[#D4AF37] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] font-semibold mb-2">
            Welcome back
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-[#2D2A26] font-light">
            {user?.full_name?.split(' ')[0]}
          </h1>
        </motion.div>

        {/* Numerology Profile */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl text-[#2D2A26]">Your Numerology Profile</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <NumerologyCard
              title="Life Path"
              number={user?.numerology?.life_path}
              description={getNumberMeaning('life_path', user?.numerology?.life_path)}
              icon={Star}
              delay={0.1}
            />
            <NumerologyCard
              title="Expression"
              number={user?.numerology?.expression}
              description={getNumberMeaning('expression', user?.numerology?.expression)}
              icon={Sparkles}
              delay={0.2}
            />
            <NumerologyCard
              title="Soul Urge"
              number={user?.numerology?.soul_urge}
              description={getNumberMeaning('soul_urge', user?.numerology?.soul_urge)}
              icon={Heart}
              delay={0.3}
            />
            <NumerologyCard
              title="Personality"
              number={user?.numerology?.personality}
              description="How others perceive your outer energy"
              icon={User}
              delay={0.4}
            />
          </div>
        </section>

        {/* Top Matches Preview */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl text-[#2D2A26]">Top Cosmic Matches</h2>
            <Link 
              to="/matches" 
              className="text-[#D4AF37] hover:text-[#B8860B] font-medium inline-flex items-center gap-1"
              data-testid="view-all-matches"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loadingMatches ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="ethereal-card rounded-2xl p-6 animate-pulse">
                  <div className="w-16 h-16 rounded-full bg-[#F3E5AB]/50 mx-auto mb-4" />
                  <div className="h-4 bg-[#F3E5AB]/50 rounded w-24 mx-auto mb-2" />
                  <div className="h-3 bg-[#F3E5AB]/30 rounded w-32 mx-auto" />
                </div>
              ))}
            </div>
          ) : topMatches.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {topMatches.map((match, i) => (
                <motion.div
                  key={match.user.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link 
                    to={`/matches/${match.user.id}`}
                    className="block ethereal-card rounded-2xl p-6 text-center hover:shadow-[0_8px_40px_rgba(212,175,55,0.25)] transition-all duration-300"
                    data-testid={`match-card-${match.user.id}`}
                  >
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center mx-auto mb-4 text-white font-heading text-2xl">
                      {match.user.full_name?.charAt(0)}
                    </div>
                    <h3 className="font-heading text-xl text-[#2D2A26] mb-1">
                      {match.user.full_name?.split(' ')[0]}
                    </h3>
                    <p className="text-[#8C857B] text-sm mb-4">
                      Life Path {match.user.numerology?.life_path}
                    </p>
                    <div className="compatibility-bar mb-2">
                      <div 
                        className="compatibility-fill" 
                        style={{ width: `${match.compatibility.overall}%` }} 
                      />
                    </div>
                    <p className="text-[#D4AF37] font-medium">
                      {match.compatibility.overall}% Compatible
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="ethereal-card rounded-2xl p-12 text-center">
              <Users className="w-12 h-12 text-[#D4AF37]/50 mx-auto mb-4" />
              <p className="text-[#8C857B]">No matches found yet. More souls are joining every day!</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default DashboardPage;
